import { GoabIcon } from '@abgov/react-components';
import { useEffect, useRef, useCallback, useState } from 'react';
import { domainKey } from '../../utils/globalUtility';
import './CanadaPostGoabInput.css';

// Global flag to prevent multiple XMLHttpRequest overrides
let pcaXhrOverridden = false;

const CanadaPostGoabInput = (props) => {
  const { provinceCodes, onPopulate, setSelectedAddress, form, width, disabled, getResourceValue } = props;
  const controlRef = useRef(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  const propsRef = useRef();
  // Prevents useCallback re-rendering controls by removing dependency array.
  propsRef.current = { provinceCodes, onPopulate, setSelectedAddress, form, getResourceValue };

 useEffect(()=>{
    // Override pca's XMLHttpRequest to add domain key header BEFORE creating the control
    // Only override if not already overridden globally
    if (domainKey && window.pca && !pcaXhrOverridden) {
      const originalXMLHttpRequest = window.pca.XMLHttpRequest || window.XMLHttpRequest;
      
      window.pca.XMLHttpRequest = function() {
        const xhr = new originalXMLHttpRequest();
        const originalOpen = xhr.open;
        const originalSetRequestHeader = xhr.setRequestHeader;
        const headers = {}; // Track all headers

        // Override setRequestHeader to track headers
        xhr.setRequestHeader = function(name, value) {
          headers[name] = value; // Track all headers
          originalSetRequestHeader.call(this, name, value);
        };
        
        xhr.open = function(method, url, ...args) {
          originalOpen.apply(this, [method, url, ...args]);

          // Add domain key header to all requests
          this.setRequestHeader('domain-key', domainKey);
        };
        
        return xhr;
      };

      pcaXhrOverridden = true;
    }
  }, [domainKey]);

  const onAddressCompleteLoad = useCallback(() => {
    // Clean up existing control to prevent multiple instances
    if (controlRef.current?.destroy) {
      controlRef.current.destroy();
    }
    
    const fields = [{ element: 'canadaPostId', field: '', mode: window.pca.fieldMode.SEARCH }];
    const options = {
      minSearch: 3,
      maxItems: 10,
      promptDelay: 400,
      populate: false,
      autoSearch: false,
      culture: 'en-ca',
      languagePreference: 'eng',
      bar: { showCountry: false, showLogo: false },
    };

    // Set the PCA host endpoint
    window.pca.host = `${process.env.REACT_APP_API_ENDPOINT.replace(/^https?:\/\//, '')}/api/AddressValidation`;
    const control = new window.pca.Address(fields, options);
    controlRef.current = control;
    
    control.listen('populate', (address) => {
      // Get current values from ref
      const { provinceCodes, onPopulate, setSelectedAddress, form, getResourceValue } = propsRef.current;
      
      if (address && (!provinceCodes || provinceCodes?.toUpperCase()?.includes(address.ProvinceCode))) {
        onPopulate({
          canadaPostId: address.Id,
          streetAddressLine1: address.Line1,
          streetAddressLine2: address.Line2,
          locality: address.City,
          postalCode: address.PostalCode,
          provinceName: address.ProvinceName,
          province: address.ProvinceCode ? `${address.CountryIso2}-${address.ProvinceCode}` : '',
          countryIso2: address.CountryIso2,
        }, form, setSelectedAddress, getResourceValue);
      }
    });
  }, []);

  useEffect(() => {
    const scriptElement = document.getElementById('canadaPostAddressComplete');
    
    if (typeof window.pca === 'undefined') {
      scriptElement?.addEventListener('load', onAddressCompleteLoad);
    } else {
      onAddressCompleteLoad();
    }

    return () => {
      scriptElement?.removeEventListener('load', onAddressCompleteLoad);
      // Clean up on unmount
      if (controlRef.current?.destroy) {
        controlRef.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const pcaDropdown = document.querySelector('.pca');
      const computedStyle = pcaDropdown ? window.getComputedStyle(pcaDropdown) : null;
      const isDropdownHidden = !pcaDropdown
        || computedStyle?.display === 'none'
        || computedStyle?.visibility === 'hidden'
        || computedStyle?.visibility === 'collapse';
  
      // 1. If the Canada Post dropdown does not exist 
      // or is hidden, do not overtake the click or dispatch keyboard events.
      if (isDropdownHidden) {
        return;
      }
  
      // Use contains() instead of composedPath().includes() — composedPath() is retargeted
      // at shadow DOM boundaries, causing identity checks to fail for GoA web components.
      const target = e.target;
      const clickedInsideInput = containerRef.current?.contains(target);
      const clickedInsidePca = pcaDropdown?.contains(target);
  
      if (!clickedInsideInput && !clickedInsidePca) {
        // Directly call PCA's hide() method rather than dispatching a keyboard event,
        // which PCA may ignore if the input has lost focus due to a GoA shadow DOM interaction.
        if (controlRef.current?.hide) {
          controlRef.current.hide();
        }
      }
    };
  
    document.addEventListener('pointerdown', handleOutsideClick);
  
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick);
    };
  }, []);


  const handleSubmit = () => {
    delete form?.errors["streetAddressLine1"];
    delete form?.errors["locality"];
    delete form?.errors["postalCode"];
    delete form?.errors["province"];
    delete form?.errors["countryIso2"];
  };

  return (
    <div
      ref={containerRef}
      className="canada-post-input--container"
      style={width ? { '--width': width } : undefined}>
      <div className="canada-post-input--input">
        <GoabIcon type="search" />
        <input ref={inputRef} type="text" id="canadaPostId" disabled={disabled} onBlur={handleSubmit} />
      </div>
    </div>
  );
};

export default CanadaPostGoabInput;
